import axiosInstance, { getApiErrorMessage } from "../auth/apiConfig";

const WEB_BASE = "/api/v1/web/ot-notes";

export const listOtReservations = async (params = {}) => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/reservations`, {
      params: {
        patientSearch: params.patientSearch || undefined,
        fromDate: params.fromDate || undefined,
        toDate: params.toDate || undefined,
        otId: params.otId || undefined,
        otTableId: params.otTableId || undefined,
      },
    });
    return response.data?.data ?? [];
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to load OT reservations"));
  }
};

export const listOtTheatres = async () => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/theatres`);
    return response.data?.data ?? [];
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to load theatres"));
  }
};

export const listOtTables = async (otId) => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/tables`, {
      params: { otId: otId || undefined },
    });
    return response.data?.data ?? [];
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to load OT tables"));
  }
};

export const searchOtSurgeries = async (query, top = 50) => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/surgeries`, {
      params: {
        q: query?.trim() || undefined,
        top,
      },
    });
    return response.data?.data ?? [];
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to search surgeries"));
  }
};

export const listAnaesthesiaTypes = async () => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/anaesthesia-types`);
    return response.data?.data ?? [];
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to load anaesthesia types"));
  }
};

export const listOtResults = async () => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/ot-results`);
    return response.data?.data ?? [];
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to load OT results"));
  }
};

export const listOtNotesByVisit = async (opdIpdId, opdIpd) => {
  try {
    const response = await axiosInstance.get(WEB_BASE, {
      params: { opdIpdId, opdIpd },
    });
    return response.data?.data ?? [];
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to load OT notes"));
  }
};

export const getOtNoteById = async (operationDetailsId) => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/${operationDetailsId}`);
    return response.data?.data ?? null;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to load OT note"));
  }
};

export const createOtNote = async (payload) => {
  try {
    const response = await axiosInstance.post(WEB_BASE, payload);
    return response.data?.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to save OT note"));
  }
};

export const updateOtNote = async (operationDetailsId, payload) => {
  try {
    const response = await axiosInstance.put(
      `${WEB_BASE}/${operationDetailsId}`,
      payload
    );
    return response.data?.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to update OT note"));
  }
};

/** Map lookup item {id,name} to SearchDropdown option. */
export const toLookupOption = (item) => {
  if (!item) return null;
  const id = item.id ?? item.value;
  const name = item.name ?? item.label ?? "";
  return {
    id,
    value: id,
    label: name,
    data: { id, name },
  };
};

/** Normalize time to HH:mm:ss for API LocalTime. */
export const toApiTime = (timeValue) => {
  if (!timeValue) return null;
  const raw = String(timeValue).trim();
  if (!raw) return null;
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(raw)) {
    const [h, m, s = "00"] = raw.split(":");
    return `${h.padStart(2, "0")}:${m.padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  const match = raw.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match) {
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const period = match[3].toUpperCase();
    if (period === "PM" && hours < 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;
    return `${String(hours).padStart(2, "0")}:${minutes}:00`;
  }
  return null;
};

export const formatDisplayTime = (timeValue) => {
  if (!timeValue) return "";
  const raw = String(timeValue);
  const timePart = raw.includes("T") ? raw.split("T")[1] : raw;
  return timePart.slice(0, 5);
};

export const mapReservationToTableRow = (row) => {
  const start = formatDisplayTime(row.surgeryStartTime);
  const end = formatDisplayTime(row.surgeryEndTime);
  return {
    reservationId: row.otReservationId,
    otReservationId: row.otReservationId,
    regId: row.regId,
    opdIpdId: row.opdIpdId,
    opdIpd: Boolean(row.opdIpd),
    patientName: row.patientName || "",
    uhid: row.uhid || "",
    opdNo: row.opIpNumber || "",
    doctorName: row.doctorName || "",
    doctorId: row.doctorId,
    patientType: row.opdIpd ? "IPD" : "OPD",
    tariffName: "—",
    companyName: "—",
    surgeryDate: row.surgeryDate || "",
    theatreName: row.theatreName || "",
    tableName: row.tableName || "",
    surgeryName: row.surgeryName || "",
    surgeryId: row.surgeryId,
    slotTime: [start, end].filter(Boolean).join(" - ") || "—",
    theatreId: row.otId,
    tableId: row.otTableId,
    otId: row.otId,
    otTableId: row.otTableId,
    anaesthetistId: row.anaesthetistId,
    surgeryStartTime: row.surgeryStartTime,
    surgeryEndTime: row.surgeryEndTime,
    duration: row.duration,
  };
};
